import React from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { FiX, FiCopy } from 'react-icons/fi';
import { toast } from 'react-toastify';
import styles from './MonitoringDashboardModal.module.css';

/**
 * SQL 의 :name 바인드 토큰을 실제 값으로 치환하고, 원본 변수명을 주석으로 남김.
 *
 * 예: `WHERE x > NOW() - INTERVAL ':minutes minutes'`
 *  →  `WHERE x > NOW() - INTERVAL '60 /* :minutes *\/ minutes'`
 *  (단, 단일 따옴표 안의 :minutes 는 SQL 표준상 식별자가 아니라 리터럴이라
 *   실제 oracledb 도 치환 안 함 — 본 함수도 따옴표 안은 건드리지 않음.)
 *
 * 처리 규칙:
 * - `--` 라인 코멘트, `/* ... *\/` 블록 코멘트, `'...'` 단일 따옴표 문자열은 그대로 보존.
 * - 그 외 위치의 `:name` 만 치환.
 * - binds 에 해당 키 없으면 원본 `:name` 유지.
 *
 * 값 포맷:
 * - number → 그대로 (`60`)
 * - boolean → 1/0
 * - null/undefined → NULL
 * - string/그 외 → 작은따옴표로 감싸고 `'` 이스케이프 (`'a''b'`)
 */
export function substituteBinds(sql, binds) {
  if (!sql) return '';
  const safeBinds = binds || {};
  return String(sql).replace(
    // 1: 라인 코멘트, 2: 블록 코멘트, 3: 문자열 리터럴, 4: 바인드 (이름은 5)
    /(--[^\n]*)|(\/\*[\s\S]*?\*\/)|('(?:''|[^'])*')|:([a-zA-Z_][a-zA-Z0-9_]*)/g,
    (match, lineCmt, blockCmt, str, bindName) => {
      if (lineCmt || blockCmt || str) return match;
      if (!bindName) return match;
      if (!Object.prototype.hasOwnProperty.call(safeBinds, bindName)) {
        return match; // 값 없음 — 원본 유지 (사용자가 바인드 누락 인지)
      }
      return `${formatSqlLiteral(safeBinds[bindName])} /* :${bindName} */`;
    }
  );
}

function formatSqlLiteral(v) {
  if (v === null || v === undefined) return 'NULL';
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : 'NULL';
  if (typeof v === 'boolean') return v ? '1' : '0';
  // 문자열 — 단일 따옴표 이스케이프
  const s = (v instanceof Date) ? v.toISOString() : String(v);
  return `'${s.replace(/'/g, "''")}'`;
}

/**
 * 패널 카드의 "SQL 보기" 아이콘 클릭 시 표시되는 모달.
 *
 * - panel.sql 에 binds 를 적용해 실제 실행된(될) SQL 을 보여줌.
 * - 복사 버튼: navigator.clipboard.writeText.
 * - overlay 클릭 또는 X 버튼으로 닫힘.
 *
 * 검증 용도 — DB 클라이언트에 그대로 붙여넣어 실행하면 패널 차트가 어떤 데이터를
 * 보여주고 있는지 확인 가능.
 */
const SqlViewModal = ({ panel, binds, onClose }) => {
  const { t } = useTranslation();
  const substituted = substituteBinds(panel?.sql || '', binds || {});
  const hasSql = !!(panel?.sql && panel.sql.trim());

  const handleCopy = () => {
    if (!hasSql) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(substituted).then(
        () => toast.success(t('monitoring:sv.copied')),
        () => toast.error(t('monitoring:sv.copyFailed'))
      );
    } else {
      toast.error(t('monitoring:sv.noClipboard'));
    }
  };

  // ESC 로 닫기
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className={styles.sqlViewOverlay} onMouseDown={onClose}>
      <div
        className={styles.sqlViewModal}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={t('monitoring:sv.title')}
      >
        <div className={styles.sqlViewHeader}>
          <span className={styles.sqlViewTitle}>
            {t('monitoring:sv.header')} {panel?.title || t('monitoring:sv.noName')}
          </span>
          <div className={styles.sqlViewActions}>
            <button
              className={styles.sqlViewIconBtn}
              onClick={handleCopy}
              title={t('monitoring:sv.copy')}
              disabled={!hasSql}
            >
              <FiCopy size={14} />
            </button>
            <button
              className={styles.sqlViewIconBtn}
              onClick={onClose}
              title={t('monitoring:sv.close')}
            >
              <FiX size={14} />
            </button>
          </div>
        </div>
        <div className={styles.sqlViewSubheader}>
          {t('monitoring:sv.bindNote1')} <code>/* :name */</code> {t('monitoring:sv.bindNote2')}
          {!hasSql && <strong> {t('monitoring:sv.notDefined')}</strong>}
        </div>
        <pre className={styles.sqlViewBody}>
          {hasSql ? substituted : t('monitoring:sv.noSql')}
        </pre>
      </div>
    </div>,
    document.body
  );
};

export default SqlViewModal;
